import { Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { TranslocoDirective } from '@ngneat/transloco';
import {
  IconAlertComponent,
  IconBackComponent,
  IconBanComponent,
  IconEyeComponent,
  IconLoadingComponent,
  IconPrintComponent,
} from '@pedix-workspace/angular-ui-icons';
import { ActionSheetComponent } from '@pedix-workspace/angular-ui-action-sheet';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { BillingInformation, EndOrderDetails, TaxVoucher } from '@pedix-workspace/utils';
import {
  getArcaVoucherTypeName,
  isTaxVoucherArca,
  TaxVoucherArca,
} from '@pedix-workspace/integrations-arca';
import { saveAs } from 'file-saver';
import { PdfModalViewerComponent } from '@pedix-workspace/angular-ui-pdf';
import { UriStoragePipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { ModalDialogComponent } from '@pedix-workspace/angular-ui-modal';
import { noop } from 'lodash-es';

@Component({
  selector: 'app-order-details-tax-voucher-actions',
  templateUrl: './order-details-tax-voucher-actions.component.html',
  styleUrls: ['./order-details-tax-voucher-actions.component.scss'],
  standalone: true,
  imports: [
    TranslocoDirective,
    ActionSheetComponent,
    ButtonComponent,
    IconBackComponent,
    IconEyeComponent,
    IconPrintComponent,
    IconBanComponent,
    IconLoadingComponent,
    PdfModalViewerComponent,
    ModalDialogComponent,
    IconAlertComponent,
  ],
})
export class OrderDetailsTaxVoucherActionsComponent {
  private uriStoragePipe = inject(UriStoragePipe);

  readonly confirmCreditNoteDialog =
    viewChild.required<ModalDialogComponent>('confirmCreditNoteDialog');

  billingInformation = input.required<BillingInformation>();
  orderDetails = input.required<EndOrderDetails>();
  taxVoucher = input.required<TaxVoucher>();

  pdfPreviewSrc = signal<string | null>(null);
  printing = signal(false);

  generateCreditNote = output<TaxVoucher>();

  readonly taxVoucherType = computed(() => {
    const taxVoucher = this.taxVoucher();

    if (isTaxVoucherArca(taxVoucher)) {
      return getArcaVoucherTypeName(taxVoucher.type) || '--';
    }

    return taxVoucher.type || '--';
  });
  readonly pdfUrl = computed(() => {
    return this.uriStoragePipe.transform(this.taxVoucher().pdfUri || '');
  });

  async onViewTaxVoucher() {
    this.pdfPreviewSrc.set(this.pdfUrl());
  }

  onDownloadTaxVoucher() {
    saveAs(this.pdfUrl(), `${this.taxVoucher().voucherRef}.pdf`);
  }

  async onPrintTaxVoucher() {
    this.printing.set(true);

    // NOTE: this import breaks SSR, so we need to import it dynamically
    const { default: printJS } = await import('print-js');

    printJS({
      printable: this.pdfUrl(),
      type: 'pdf',
      onLoadingEnd: () => {
        this.printing.set(false);
      },
    });
  }

  onGenerateCreditNote() {
    this.confirmCreditNoteDialog()
      .open()
      .then(() => {
        this.generateCreditNote.emit(this.taxVoucher());
      })
      .catch(() => {});
  }
}
